import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { IUser } from '../model/user.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { ISettings } from '../model/user-settings.model';
import { IUserLogin } from '../model/user-login.model';
import { IUserRegister } from '../model/user-register.model';
import { IWaitingList } from '../model/waitingList.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  currentUser$!: Observable<IUser>;
  authService = inject(AuthenticationService);

  constructor (private http: HttpClient) {}

  login(user: IUserLogin): Observable<IUser> {
    let options = { headers: new HttpHeaders( {'Content-type': 'application/json' }) };
    return this.http.post<IUser>( environment.API_URL + '/api/user/login', user, options).pipe(
      tap((user: IUser) => {
        localStorage.setItem('token', user.token ?? '');
      })
    )
  }

  logout(): void {
    this.authService.clearUserToken();
  }

  retryLogin(): Observable<IUser> {
    const token = this.authService.getUserToken();

    let options = { headers: new HttpHeaders( {
      'Content-type': 'application/json',
      'Authorization': `Bearer ${token}`}) };

    return this.http.get<IUser>( environment.API_URL + '/api/user/retryLogin', options).pipe(
      tap((user: IUser) => {
        localStorage.setItem('token', user.token ?? '');
      })
    )
  }

  register (user: IUserRegister): Observable<IUser> {
    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json' }) };
    return this.http.post<IUser>( environment.API_URL + '/api/user/register', user, options);
  }

  changePicture(imageData: string | File): Observable<string> {
    const token = this.authService.getUserToken();

    const formData = new FormData();

    if (imageData instanceof File) {
      formData.append('imageFile', imageData, imageData.name);
    } else {
      formData.append('imageDataUrl', imageData);
    }


    let options = {
        headers: new HttpHeaders( {
            'Authorization': `Bearer ${token}`
        }),
        contentType: 'multipart/form-data',
        body: formData
     };

    return this.http.post<string>( environment.API_URL + '/api/user/updatePicture', formData, options);
  }

  changeGeneralData (name: string, email: string, login: string): Observable<IUser> {
    const token = this.authService.getUserToken();

    let user = {
        name: name,
        email: email,
        login: login
    };

    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`}) };

    return this.http.post<IUser>( environment.API_URL + '/api/user/update', user, options);
  }

  changePassword (password: string, newPassword: string): Observable<boolean> {
    const token = this.authService.getUserToken();

    let user = {
        currentPassword: password,
        newPassword: newPassword
    };

    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`}) };

    return this.http.post<boolean>( environment.API_URL + '/api/user/changePassword', user, options);
  }

  changeSettings (timeout: number, publicProfile: boolean, nostrPK: string): Observable<ISettings> {
    const token = this.authService.getUserToken();

    let settings = {
        timeout: timeout,
        publicProfile: publicProfile,
        nostrPK: nostrPK
    };

    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`}) };

    return this.http.post<ISettings>( environment.API_URL + '/api/user/updateSettings', settings, options);
  }

  getSettings (): Observable<ISettings> {
    const token = this.authService.getUserToken();

      let options = { headers: new HttpHeaders( {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${token}`}) };

      return this.http.get<ISettings>( environment.API_URL + '/api/user/settings',  options);
  }

  addLoggedTime(): Observable<object> | undefined {
    const token = this.authService.getUserToken();
    const loginTime = localStorage.getItem('LoginTime');
    if (loginTime) {
      const elapsedTime = Math.round((new Date().getTime() - Number(loginTime)) / 1000 / 60);

      let loggedTime = {
          elapsedTime: elapsedTime,
      };

      let options = { headers: new HttpHeaders( {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${token}`}) };

      return this.http.post( environment.API_URL + '/api/user/addLoggedTime', loggedTime, options);
    }
    return undefined;
  }

  checkLogin (login: string): Observable<boolean> {
    const token = this.authService.getUserToken();

    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`}) };
    return this.http.get<boolean>( environment.API_URL + '/api/user/checklogin?login=' + login,  options);
  }

  activate (token: string | null): Observable<boolean> {
    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json'}) };
    return this.http.get<boolean>( environment.API_URL + '/api/user/activate?token=' + token,  options);
  }

  getById (userId:string): Observable<IUser> {
    const token = this.authService.getUserToken();

      let options = { headers: new HttpHeaders( {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${token}`}) };

      return this.http.get<IUser>( environment.API_URL + `/api/user/${userId}`,  options);
  }

  registerWaitingList (user: IWaitingList): Observable<IWaitingList> {
    let options = { headers: new HttpHeaders( {
        'Content-type': 'application/json' }) };
    return this.http.post<IWaitingList>( environment.API_URL + '/api/user/waitingListRegister', user, options);
  }

  loginWithSso(token: string): Observable<IUser>  {
    return this.http.post<IUser>(environment.API_URL + '/api/sso/login', { token }).pipe(
        tap((user: IUser) => {
        localStorage.setItem('token', user.token ?? '');
      })
    );
  }
}
